import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, ErrorState, Pill,
    CopperButton, SlateButton, TextInput, Modal, Field,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { ArrowLeft, Lock, Send, CheckCircle2, AlertTriangle, Sparkles, Edit, Undo2, FileDown, FileSpreadsheet } from "lucide-react";
import { fmtINR, fmtKg, fmtDate, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

const STATUS_TONE = {
    DRAFT: { tone: "slate", label: "Draft" },
    DIRECTOR_REVIEW: { tone: "amber", label: "Director Review" },
    DIRECTOR_APPROVED: { tone: "emerald", label: "Director Approved (Locked)" },
    HELD_BY_DIRECTOR: { tone: "red", label: "Held by Director" },
};

const ADVICE_TONE = {
    ACCEPT: { tone: "emerald", icon: CheckCircle2 },
    COUNTER: { tone: "red", icon: AlertTriangle },
    REVIEW: { tone: "amber", icon: AlertTriangle },
};

export default function SettlementDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [s, setS] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [approveOpen, setApproveOpen] = useState(false);
    const [holdOpen, setHoldOpen] = useState(false);
    const [comment, setComment] = useState("");
    const [breakdownOpen, setBreakdownOpen] = useState(false);

    const load = () =>
        api.get(`/settlements/${id}`)
            .then((r) => setS(r.data))
            .catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, [id]);

    if (error) return <div className="p-6"><ErrorState message={error} onRetry={load} /></div>;
    if (!s) return <LoadingState />;

    const c = s.computed || {};
    const st = STATUS_TONE[s.status] || { tone: "slate", label: s.status };
    const locked = s.locked === true;
    const canEdit = !locked && hasRole(user, "CORPORATE_ADMIN", "FINANCE_PRICING") && s.status === "DRAFT";
    const canSubmit = !locked && hasRole(user, "CORPORATE_ADMIN", "FINANCE_PRICING") && s.status === "DRAFT";
    const canWithdraw = !locked && hasRole(user, "CORPORATE_ADMIN", "FINANCE_PRICING") && s.status === "DIRECTOR_REVIEW";
    const canApprove = !locked && hasRole(user, "DIRECTOR", "CORPORATE_ADMIN") && s.status === "DIRECTOR_REVIEW";

    const callAction = async (path, body) => {
        setBusy(true);
        try {
            await api.post(`/settlements/${id}/${path}`, body || {});
            toast.success("Done");
            setApproveOpen(false); setHoldOpen(false); setComment("");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    const download = async (kind /* "pdf" | "xlsx" */) => {
        setBusy(true);
        try {
            const path = kind === "pdf" ? "synopsis.pdf" : "export.xlsx";
            const res = await api.get(`/settlements/${id}/${path}`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: res.headers["content-type"] });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${s.reference}.${kind === "pdf" ? "pdf" : "xlsx"}`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(`${kind.toUpperCase()} downloaded`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="settlement-detail-page">
            <PageHeader
                eyebrow={s.settlement_type + " · " + s.customer_code}
                title={s.reference}
                subtitle={`Period: ${fmtDate(s.period_from)} → ${fmtDate(s.period_to)}`}
                actions={
                    <div className="flex items-center gap-2">
                        <SlateButton variant="ghost" onClick={() => navigate("/settlement")}>
                            <ArrowLeft className="h-4 w-4" /> Back
                        </SlateButton>
                        <SlateButton onClick={() => download("xlsx")} disabled={busy} data-testid="sett-detail-xlsx-btn">
                            <FileSpreadsheet className="h-4 w-4" /> Excel
                        </SlateButton>
                        <SlateButton onClick={() => download("pdf")} disabled={busy} data-testid="sett-detail-pdf-btn">
                            <FileDown className="h-4 w-4" /> Synopsis PDF
                        </SlateButton>
                        {canEdit && (
                            <SlateButton onClick={() => navigate(`/settlement/${id}/edit`)} data-testid="sett-detail-edit-btn">
                                <Edit className="h-4 w-4" /> Edit
                            </SlateButton>
                        )}
                        {canSubmit && (
                            <CopperButton onClick={() => callAction("submit")} disabled={busy} data-testid="sett-detail-submit-btn">
                                <Send className="h-4 w-4" /> Submit to Director
                            </CopperButton>
                        )}
                        {canWithdraw && (
                            <SlateButton onClick={() => callAction("withdraw")} disabled={busy} data-testid="sett-detail-withdraw-btn">
                                <Undo2 className="h-4 w-4" /> Withdraw
                            </SlateButton>
                        )}
                        {canApprove && (
                            <>
                                <SlateButton variant="danger" onClick={() => setHoldOpen(true)} disabled={busy} data-testid="sett-detail-hold-btn">
                                    Hold
                                </SlateButton>
                                <CopperButton onClick={() => setApproveOpen(true)} disabled={busy} data-testid="sett-detail-approve-btn">
                                    <Lock className="h-4 w-4" /> Approve & Lock
                                </CopperButton>
                            </>
                        )}
                    </div>
                }
            />

            {locked && (
                <Card className="mb-4 p-3 bg-emerald-50 border-emerald-200">
                    <div className="flex items-start gap-2.5 text-emerald-900">
                        <Lock className="h-4 w-4 mt-0.5 shrink-0" />
                        <div className="text-[13px]">
                            <div className="font-semibold">Locked · Director-approved on {fmtDateTime(s.director_approved_at)}</div>
                            <div className="text-[12px] mt-0.5">By {s.director_approved_by_name}. Per Business Rule 5, this settlement is permanently sealed: no edits, no rollback.</div>
                        </div>
                    </div>
                </Card>
            )}

            {/* Summary cards: Total Claim, DMC and Weight-Gap Recovery drill down
                into the per-grade / per-part breakdown behind each number on click. */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <SummaryCard label="Total Claim" value={fmtINR(c.total_claim || 0)} accent onClick={() => setBreakdownOpen(true)} />
                <SummaryCard label="DMC (Full Period)" value={fmtINR(c.dmc_full_period || 0)} sub={`${c.months_in_period || 1} mo @ ${fmtINR(c.dmc_per_month || 0)}/mo`} onClick={() => setBreakdownOpen(true)} />
                <SummaryCard label="Weight-Gap Recovery" value={fmtINR(c.weight_gap_recovery || 0)} onClick={() => setBreakdownOpen(true)} />
                <SummaryCard label="Status" valueEl={<Pill tone={st.tone}>{st.label}</Pill>} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    {/* Rate Matrix with AI advice */}
                    <Card>
                        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                            <div className="font-display text-[15px] font-semibold">Rate Matrix · Intelligent Negotiation Advisor</div>
                            <div className="eyebrow text-slate-500">{c.advice_counts ? `${c.advice_counts.ACCEPT || 0}A · ${c.advice_counts.COUNTER || 0}C · ${c.advice_counts.REVIEW || 0}R` : ""}</div>
                        </div>
                        {c.strategy_summary && (
                            <div className="px-4 py-2 bg-[hsl(var(--copper-soft))]/40 border-b border-slate-200 text-[12px] text-slate-700 flex items-start gap-2">
                                <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--copper))] mt-0.5 shrink-0" />
                                <span>{c.strategy_summary}</span>
                            </div>
                        )}
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/60">
                                    <th className="eyebrow text-left py-2.5 px-3">Grade</th>
                                    <th className="eyebrow text-right py-2.5 px-3">Last</th>
                                    <th className="eyebrow text-right py-2.5 px-3">Proposed</th>
                                    <th className="eyebrow text-right py-2.5 px-3">SIAM</th>
                                    <th className="eyebrow text-right py-2.5 px-3">Δ</th>
                                    <th className="eyebrow text-right py-2.5 px-3">Steel kg</th>
                                    <th className="eyebrow text-right py-2.5 px-3">DMC / mo</th>
                                    <th className="eyebrow text-left py-2.5 px-3">Advice</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(c.rate_matrix_computed || s.rate_matrix || []).map((r, i) => {
                                    const at = ADVICE_TONE[r.advice] || ADVICE_TONE.REVIEW;
                                    const Icon = at.icon;
                                    return (
                                        <tr key={i} className="border-b border-slate-100">
                                            <td className="py-2.5 px-3 font-mono text-[13px]">{r.grade_code}</td>
                                            <td className="py-2.5 px-3 text-right font-mono tabular-nums text-[12px]">{fmtINR(r.last_agreed)}</td>
                                            <td className="py-2.5 px-3 text-right font-mono tabular-nums text-[12px] font-semibold">{fmtINR(r.proposed)}</td>
                                            <td className="py-2.5 px-3 text-right font-mono tabular-nums text-[12px] text-slate-500">{r.siam ? fmtINR(r.siam) : "—"}</td>
                                            <td className={`py-2.5 px-3 text-right font-mono tabular-nums text-[12px] ${r.movement > 0 ? "text-red-600" : r.movement < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                                                {r.movement > 0 ? "+" : ""}{(r.movement || 0).toFixed(2)}
                                            </td>
                                            <td className="py-2.5 px-3 text-right font-mono tabular-nums text-[12px]">{fmtKg(r.steel_kg || 0)}</td>
                                            <td className="py-2.5 px-3 text-right font-mono tabular-nums text-[12px]">{fmtINR(r.dmc_per_month || 0)}</td>
                                            <td className="py-2.5 px-3">
                                                {r.advice && (
                                                    <div className="flex items-start gap-1.5">
                                                        <Pill tone={at.tone} className="inline-flex items-center gap-1">
                                                            <Icon className="h-3 w-3" /> {r.advice}
                                                        </Pill>
                                                    </div>
                                                )}
                                                {r.reason && <div className="text-[11px] text-slate-500 mt-1 max-w-xs">{r.reason}</div>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Card>

                    {/* Sale register */}
                    {(c.sale_register_computed && c.sale_register_computed.length > 0) && (
                        <Card>
                            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                                <div className="font-display text-[15px] font-semibold">Sale Register</div>
                                <div className="text-[11px] text-slate-500">{c.parts_matched || 0} matched · {c.parts_unmatched || 0} unmatched</div>
                            </div>
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50/60">
                                        <th className="eyebrow text-left py-2.5 px-3">Part No.</th>
                                        <th className="eyebrow text-left py-2.5 px-3">Match</th>
                                        <th className="eyebrow text-left py-2.5 px-3">Grade</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Qty</th>
                                        <th className="eyebrow text-right py-2.5 px-3">BOM kg</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Cust kg</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Gap kg</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Gap Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {c.sale_register_computed.map((r, i) => (
                                        <tr key={i} className="border-b border-slate-100">
                                            <td className="py-2 px-3 font-mono text-[12px]">{r.part_number}</td>
                                            <td className="py-2 px-3">
                                                {r.matched
                                                    ? <Pill tone="emerald">Matched</Pill>
                                                    : <Pill tone="red">Unmatched</Pill>}
                                            </td>
                                            <td className="py-2 px-3 text-[12px] font-mono">{r.grade_code || "—"}</td>
                                            <td className="py-2 px-3 text-right font-mono tabular-nums text-[12px]">{(r.qty || 0).toLocaleString()}</td>
                                            <td className="py-2 px-3 text-right font-mono tabular-nums text-[12px]">{(r.bom_weight_kg || 0).toFixed(3)}</td>
                                            <td className="py-2 px-3 text-right font-mono tabular-nums text-[12px]">{(r.customer_weight_kg || 0).toFixed(3)}</td>
                                            <td className={`py-2 px-3 text-right font-mono tabular-nums text-[12px] ${(r.weight_gap_kg || 0) > 0 ? "text-emerald-600" : "text-slate-400"}`}>
                                                {(r.weight_gap_kg || 0).toFixed(3)}
                                            </td>
                                            <td className="py-2 px-3 text-right font-mono tabular-nums text-[12px]">{fmtINR(r.gap_value || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Card>
                    )}
                </div>

                {/* Right column */}
                <div className="space-y-4">
                    <Card className="p-4">
                        <div className="eyebrow text-slate-500 mb-2">SIAM REFERENCE</div>
                        <dl className="text-[13px] space-y-1.5">
                            <Row label="Month" v={s.siam_rates?.rate_month || "—"} mono />
                            <Row label="HR ≤ 10mm" v={fmtINR(s.siam_rates?.hr_le_10mm || 0)} mono />
                            <Row label="HR > 10mm" v={fmtINR(s.siam_rates?.hr_gt_10mm || 0)} mono />
                            <Row label="CR Sheet" v={fmtINR(s.siam_rates?.cr_sheet || 0)} mono />
                            <Row label="Scrap" v={fmtINR(s.siam_rates?.scrap || 0)} mono />
                        </dl>
                    </Card>

                    <Card className="p-4">
                        <div className="eyebrow text-slate-500 mb-2">METADATA</div>
                        <dl className="text-[13px] space-y-1.5">
                            <Row label="Created" v={fmtDate(s.created_at)} />
                            <Row label="Created by" v={s.created_by_name || "—"} />
                            {s.submitted_at && <Row label="Submitted" v={fmtDateTime(s.submitted_at)} />}
                            {s.submitted_by_name && <Row label="By" v={s.submitted_by_name} />}
                            {s.director_approved_at && <Row label="Director sign-off" v={fmtDateTime(s.director_approved_at)} />}
                            {s.notes && <Row label="Notes" v={s.notes} />}
                        </dl>
                    </Card>

                    {(s.approval_history || []).length > 0 && (
                        <Card className="p-4">
                            <div className="eyebrow text-slate-500 mb-2">AUDIT TRAIL</div>
                            <ul className="space-y-2">
                                {s.approval_history.map((h, i) => (
                                    <li key={i} className="text-[12px] border-l-2 border-[hsl(var(--copper))] pl-2.5">
                                        <div className="font-semibold text-slate-800">{h.action.replace(/_/g, " ")}</div>
                                        <div className="text-slate-500">{h.by_name} · {h.by_role}</div>
                                        <div className="text-slate-400 font-mono text-[10px]">{fmtDateTime(h.at)}</div>
                                        {h.comment && <div className="text-slate-600 mt-0.5 italic">&ldquo;{h.comment}&rdquo;</div>}
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    )}
                </div>
            </div>

            {/* Approve modal */}
            <Modal
                open={approveOpen}
                onClose={() => setApproveOpen(false)}
                title="Approve & Lock Settlement (Rule 5)"
                footer={
                    <>
                        <SlateButton onClick={() => setApproveOpen(false)} variant="ghost">Cancel</SlateButton>
                        <CopperButton onClick={() => callAction("director-approve", { comment })} disabled={!comment.trim() || busy} data-testid="sett-approve-confirm-btn">
                            <Lock className="h-4 w-4" /> Approve & Lock Permanently
                        </CopperButton>
                    </>
                }
            >
                <div className="space-y-3 text-[13px]">
                    <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900 text-[12px]">
                        <strong>Warning:</strong> Director approval permanently locks this settlement. No further edits will be allowed.
                    </div>
                    <Field label="Approval Comment" required>
                        <TextInput value={comment} onChange={(e) => setComment(e.target.value)} placeholder="e.g. Counter accepted at SIAM rate for HR plates…" data-testid="sett-approve-comment" />
                    </Field>
                </div>
            </Modal>

            <Modal
                open={holdOpen}
                onClose={() => setHoldOpen(false)}
                title="Hold Settlement"
                footer={
                    <>
                        <SlateButton onClick={() => setHoldOpen(false)} variant="ghost">Cancel</SlateButton>
                        <SlateButton variant="danger" onClick={() => callAction("director-hold", { comment })} disabled={!comment.trim() || busy} data-testid="sett-hold-confirm-btn">
                            Hold
                        </SlateButton>
                    </>
                }
            >
                <Field label="Hold Comment" required>
                    <TextInput value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Reason for holding…" data-testid="sett-hold-comment" />
                </Field>
            </Modal>

            <ClaimBreakdownModal open={breakdownOpen} onClose={() => setBreakdownOpen(false)} c={c} />
        </div>
    );
}

function SummaryCard({ label, value, valueEl, sub, accent, onClick }) {
    return (
        <Card hover={!!onClick} onClick={onClick} className="p-4">
            <div className="eyebrow text-slate-500 flex items-center justify-between">
                {label}
                {onClick && <span className="text-[9.5px] normal-case tracking-normal text-slate-400">Click for breakdown</span>}
            </div>
            <div className={`mt-1 font-mono tabular-nums text-[22px] font-semibold ${accent ? "text-[hsl(var(--copper))]" : "text-slate-900"}`}>
                {valueEl || value}
            </div>
            {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
        </Card>
    );
}

function Row({ label, v, mono }) {
    return (
        <div className="flex justify-between gap-3">
            <dt className="text-slate-500">{label}</dt>
            <dd className={mono ? "font-mono tabular-nums text-slate-800" : "text-slate-800 text-right"}>{v}</dd>
        </div>
    );
}

function ClaimBreakdownModal({ open, onClose, c }) {
    const gapRows = (c.sale_register_computed || []).filter((r) => (r.gap_value || 0) > 0);
    return (
        <Modal open={open} onClose={onClose} title="Claim Breakup" size="lg">
            <div className="space-y-4">
                <p className="text-[12.5px] text-slate-600">
                    Decomposes the Total Claim into its two drivers: steel rate movement (DMC), grade by grade,
                    and weight-gap recovery, part by part.
                </p>

                <div className="rounded-md border border-slate-200 overflow-hidden">
                    <div className="grid grid-cols-3 divide-x divide-slate-200 bg-slate-50">
                        <div className="px-4 py-3">
                            <div className="eyebrow text-slate-500">DMC (RATE-DRIVEN)</div>
                            <div className="font-mono tabular-nums text-base font-semibold">{fmtINR(c.dmc_full_period || 0)}</div>
                        </div>
                        <div className="px-4 py-3">
                            <div className="eyebrow text-slate-500">WEIGHT-GAP RECOVERY</div>
                            <div className="font-mono tabular-nums text-base font-semibold">{fmtINR(c.weight_gap_recovery || 0)}</div>
                        </div>
                        <div className="px-4 py-3">
                            <div className="eyebrow text-slate-500">TOTAL CLAIM</div>
                            <div className="font-mono tabular-nums text-base font-semibold text-[hsl(var(--copper))]">{fmtINR(c.total_claim || 0)}</div>
                        </div>
                    </div>
                </div>

                <div>
                    <div className="eyebrow text-slate-500 mb-2">STEEL RATE DRIVER · BY GRADE</div>
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="border-b border-slate-200 text-slate-500">
                                <th className="text-left py-1.5">Grade</th>
                                <th className="text-right py-1.5">Rate Movement</th>
                                <th className="text-right py-1.5">Steel kg</th>
                                <th className="text-right py-1.5">DMC (Full Period)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(c.rate_matrix_computed || []).map((r, i) => (
                                <tr key={i} className="border-b border-slate-100">
                                    <td className="py-1.5 font-mono font-semibold">{r.grade_code}</td>
                                    <td className={`py-1.5 text-right font-mono tabular-nums ${r.movement > 0 ? "text-red-600" : r.movement < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                                        {r.movement > 0 ? "+" : ""}{(r.movement || 0).toFixed(2)}/kg
                                    </td>
                                    <td className="py-1.5 text-right font-mono tabular-nums">{(r.steel_kg || 0).toLocaleString()}</td>
                                    <td className="py-1.5 text-right font-mono tabular-nums font-semibold">{fmtINR(r.dmc_cumulative || 0)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div>
                    <div className="eyebrow text-slate-500 mb-2">WEIGHT DRIVER · BY PART (GAP &gt; 0 ONLY)</div>
                    {gapRows.length === 0 && (
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-500">
                            No part in this settlement's sale register has a recoverable weight gap.
                        </div>
                    )}
                    {gapRows.length > 0 && (
                        <table className="w-full text-[12px]">
                            <thead>
                                <tr className="border-b border-slate-200 text-slate-500">
                                    <th className="text-left py-1.5">Part Number</th>
                                    <th className="text-right py-1.5">Gap kg</th>
                                    <th className="text-right py-1.5">Qty</th>
                                    <th className="text-right py-1.5">Gap Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {gapRows.map((r, i) => (
                                    <tr key={i} className="border-b border-slate-100">
                                        <td className="py-1.5 font-mono font-semibold">{r.part_number}</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums">{(r.weight_gap_kg || 0).toFixed(3)}</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums">{(r.qty || 0).toLocaleString()}</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums font-semibold">{fmtINR(r.gap_value || 0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </Modal>
    );
}
